/**
 * RadiusAnalysisPanel — Comparables aggregated in concentric radii (1/3/5 km).
 *
 * Consumes: GET /api/market/radius-analysis/{project_id}
 * Shows a card per radius with: count, price stats, absorption, top comps by distance.
 */
import { useMarketRadius } from '../lib/queries';
import { Loader2, MapPin, Building2, TrendingUp, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

function fmtMoney(n) {
  return n != null ? '$' + new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n) : '—';
}
function fmtD(n, d = 1) {
  return n != null ? Number(n).toFixed(d) : '—';
}

const RADIUS_STYLES = {
  '1km': { color: '#0DBD8B', bg: 'rgba(13,189,139,0.08)', label: 'Competencia directa' },
  '3km': { color: '#6366F1', bg: 'rgba(99,102,241,0.08)', label: 'Mercado cercano' },
  '5km': { color: '#94A3B8', bg: 'rgba(148,163,184,0.08)', label: 'Contexto ampliado' },
};

function RadiusCard({ data }) {
  const style = RADIUS_STYLES[data.radius] || RADIUS_STYLES['5km'];
  const stats = data.stats || {};
  const hasData = data.comparables_count > 0;
  const topComps = (data.comparables || []).slice().sort((a, b) => (a.distance_m ?? 999999) - (b.distance_m ?? 999999)).slice(0, 4);

  return (
    <Card
      className="rounded-xl border-border/50 shadow-sm h-full"
      data-testid={`radius-card-${data.radius}`}
      style={{ borderLeft: `3px solid ${style.color}` }}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" style={{ color: style.color }} />
            <div>
              <div className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                {data.radius}
              </div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{style.label}</div>
            </div>
          </div>
          <Badge
            data-testid={`radius-count-${data.radius}`}
            className="text-[10px] tabular-nums"
            style={{ background: style.bg, color: style.color, border: 'none' }}
          >
            {data.comparables_count} {data.comparables_count === 1 ? 'comp.' : 'comps.'}
          </Badge>
        </div>

        {hasData ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  Precio/ud prom.
                </div>
                <div className="text-sm font-display font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                  {fmtMoney(stats.avg_unit_price)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  Precio/m² prom.
                </div>
                <div className="text-sm font-display font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                  {fmtMoney(stats.avg_price_m2)}
                </div>
                {stats.min_price_m2 && stats.max_price_m2 && (
                  <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                    {fmtMoney(stats.min_price_m2)} — {fmtMoney(stats.max_price_m2)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  Absorción
                </div>
                <div className="text-sm font-display font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                  {stats.avg_absorption != null ? `${fmtD(stats.avg_absorption)} uds/mes` : '—'}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  Inventario
                </div>
                <div className="text-sm font-display font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_inventory != null ? `${stats.total_inventory} uds` : '—'}
                </div>
              </div>
            </div>

            {topComps.length > 0 && (
              <div className="pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <div className="text-[9px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                  Cerca del proyecto
                </div>
                <div className="space-y-1.5">
                  {topComps.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`radius-comp-${data.radius}-${i}`}>
                      <span className="truncate" style={{ color: 'var(--text-primary)' }}>{c.name || '—'}</span>
                      <span className="tabular-nums shrink-0" style={{ color: 'var(--text-muted)' }}>
                        {c.distance_m != null ? `${(c.distance_m / 1000).toFixed(1)} km` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <Building2 className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Sin comparables en este radio</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function RadiusAnalysisPanel({ projectId }) {
  const { data, isLoading, isError, error } = useMarketRadius(projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="radius-loading">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--market-primary)' }} />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="rounded-xl border-border/50" data-testid="radius-error">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--warning-color)' }} />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Error al cargar el análisis por radio</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{error?.message || 'Intenta de nuevo'}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.has_data === false) {
    return (
      <Card className="rounded-xl border-border/50" data-testid="radius-empty">
        <CardContent className="py-8 text-center">
          <MapPin className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--warning-color)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>Sin coordenadas del proyecto</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {data?.message || 'Verifica la ubicación del proyecto y geocodifícalo para ver el análisis por radios.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const radii = data.radii || [];
  const outside = data.outside_count || 0;
  const totalComps = data.total_comparables || 0;
  const withCoords = radii.reduce((s, r) => s + r.comparables_count, 0);

  return (
    <div className="space-y-5" data-testid="radius-analysis-panel">
      {/* Summary */}
      <div className="flex items-center gap-4 p-3 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
        <TrendingUp className="w-4 h-4 shrink-0" style={{ color: 'var(--market-primary)' }} />
        <div className="flex-1 flex flex-wrap gap-4 text-xs">
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Comparables geocodificados: </span>
            <span className="font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{withCoords} / {totalComps}</span>
          </div>
          {outside > 0 && (
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Fuera de 5 km: </span>
              <span className="font-semibold tabular-nums" style={{ color: 'var(--warning-color)' }}>{outside}</span>
            </div>
          )}
        </div>
      </div>

      {/* Radius cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {radii.map(r => <RadiusCard key={r.radius} data={r} />)}
      </div>
    </div>
  );
}

export default RadiusAnalysisPanel;
